import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Card,
  CardContent,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Code as CodeIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  TableChart,
  FormatColorText,
  FormatColorFill,
  Undo,
  Redo,
  FormatSize,
  FormatQuote,
  CloudUpload,
  InsertLink,
  AspectRatio,
  PhotoSizeSelectActual,
  History,
  AddPhotoAlternate,
  ViewColumn,
  ArrowDropDown,
  Close,
  DeleteSweep,
} from '@mui/icons-material';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';

// Gelişmiş HTML Editörü Component
interface HTMLEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  companyId?: string | null; // Firebase Storage için
}

type ImageAlign = 'left' | 'center' | 'right' | 'inline';

// Sütun (grid) düzenleri — flex oranları
const COLUMN_LAYOUTS: { id: string; label: string; ratios: number[] }[] = [
  { id: 'two', label: '2 Sütun (%50 - %50)', ratios: [1, 1] },
  { id: 'three', label: '3 Sütun (%33 - %33 - %33)', ratios: [1, 1, 1] },
  { id: 'left-wide', label: 'Sol Geniş / Sağ Dar (%70 - %30)', ratios: [7, 3] },
  { id: 'right-wide', label: 'Sol Dar / Sağ Geniş (%30 - %70)', ratios: [3, 7] },
];

const HTMLEditor: React.FC<HTMLEditorProps> = ({ value, onChange, placeholder, disabled }) => {
  const [isCodeView, setIsCodeView] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showFontSizePicker, setShowFontSizePicker] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [customFontSize, setCustomFontSize] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'upload'>('url');
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageResizeDialogOpen, setImageResizeDialogOpen] = useState(false);
  const [imageWidth, setImageWidth] = useState<string>('');
  const [imageHeight, setImageHeight] = useState<string>('');
  const [imageAlign, setImageAlign] = useState<ImageAlign>('inline');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState<number>(1);
  const [uploadedImages, setUploadedImages] = useState<Array<{url: string, name: string, uploadDate: string}>>([]);
  const [showUploadedImages, setShowUploadedImages] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  // Seçili resim için kaplama (overlay) konumu — köşeden boyutlandırma tutamacı ve hızlı hizalama için
  const [overlayRect, setOverlayRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  // Seçili sütun (silme kaplaması için)
  const [selectedColumn, setSelectedColumn] = useState<HTMLElement | null>(null);
  const [columnRect, setColumnRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  // Aktif biçim durumları (kalın/italik/altı çizili vb. buton vurgusu için)
  const [activeFormats, setActiveFormats] = useState<{ [k: string]: boolean }>({});
  const [customTextColor, setCustomTextColor] = useState('#000000');
  const [customBgColor, setCustomBgColor] = useState('#FFFF00');

  const editorRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);
  const bgColorPickerRef = React.useRef<HTMLDivElement>(null);
  const textColorInputRef = React.useRef<HTMLInputElement>(null);
  const bgColorInputRef = React.useRef<HTMLInputElement>(null);
  const fontSizePickerRef = React.useRef<HTMLDivElement>(null);
  const columnPickerRef = React.useRef<HTMLDivElement>(null);
  // Editör odakta mı? DOM senkronu yalnızca odak dışındayken yapılır; böylece
  // yazarken/işlem yaparken imleç sıçraması ve yeniden giriş (re-entrancy)
  // kaynaklı donmalar engellenir.
  const isFocusedRef = React.useRef(false);
  // Diyalog (link/resim/tablo) açılırken imleç konumu kaybolmasın diye saklanır.
  const savedRangeRef = React.useRef<Range | null>(null);
  // Resim köşeden boyutlandırma sürükleme durumu
  const resizeStateRef = React.useRef<{ startX: number; startWidth: number; aspect: number } | null>(null);

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#CCFF00', '#66FF00',
    '#00FF00', '#00FF66', '#00FFCC', '#00FFFF', '#00CCFF', '#0066FF',
    '#0000FF', '#6600FF', '#CC00FF', '#FF00FF', '#FF00CC', '#FF0066'
  ];

  const fontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];

  const emitChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Toolbar butonlarında editörün odağını (ve dolayısıyla seçimi) kaybetmemek için.
  const preventBlur = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // İmleç/seçim konumunu sakla (editör içindeyse).
  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };

  // Saklanan imleç konumunu geri yükle; yoksa içeriğin sonuna al.
  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    if (savedRangeRef.current && el.contains(savedRangeRef.current.commonAncestorContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // HTML parçasını imleç konumuna güvenli şekilde ekler (execCommand'a bağlı
  // kalmadan). Diyalog kapandıktan sonra çağrılır; focus-trap ile çakışmaz.
  const insertHtmlAtSelection = (html: string) => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();

      const temp = document.createElement('div');
      temp.innerHTML = html;

      const frag = document.createDocumentFragment();
      let lastNode: ChildNode | null = null;
      while (temp.firstChild) {
        lastNode = frag.appendChild(temp.firstChild);
      }
      range.insertNode(frag);

      // İmleci eklenen içeriğin sonuna taşı.
      if (lastNode) {
        const newRange = document.createRange();
        newRange.setStartAfter(lastNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        savedRangeRef.current = newRange.cloneRange();
      }
    } else {
      el.insertAdjacentHTML('beforeend', html);
    }

    emitChange();
  };

  // Aktif biçim durumlarını (seçime göre) güncelle — buton vurgusu için.
  const updateActiveFormats = () => {
    try {
      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
      });
    } catch {
      /* bazı tarayıcılarda desteklenmeyebilir */
    }
  };

  // Editör içinde imleç/seçim değiştiğinde: seçim sakla + aktif biçimleri güncelle
  // + seçili resim/sütun kaplamalarını yeniden konumlandır.
  const handleSelectionActivity = () => {
    saveSelection();
    updateActiveFormats();
    if (selectedImage) updateOverlayRect(selectedImage);
    if (selectedColumn) updateColumnRect(selectedColumn);
  };

  // Biçimlendirme komutları (kalın, italik, hizalama, liste vb.)
  const handleCommand = (command: string, commandValue?: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // Saklanan seçimi geri yükle — böylece toolbar'a tıklarken seçim korunur.
    restoreSelection();
    document.execCommand(command, false, commandValue);
    emitChange();
    updateActiveFormats();
  };

  const handleContentChange = () => {
    emitChange();
    if (selectedImage) updateOverlayRect(selectedImage);
    if (selectedColumn) updateColumnRect(selectedColumn);
  };

  // ✅ Value dışarıdan değiştiğinde (prop olarak) editörü güncelle — yalnızca
  // editör odak dışındayken. Odaktayken DOM'a yazmak imleci bozar.
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isFocusedRef.current) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
  }, [value]);

  // Metin hizalama — execCommand ile blok düzeyinde uygulanır (innerHTML'e yazılır
  // ve kaydedilir). Eski "tüm içeriği div'e sar" yaklaşımı her tıkta iç içe div
  // üretiyordu; bu düzeltilmiştir.
  const handleContentAlignment = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    const command =
      alignment === 'left' ? 'justifyLeft' :
      alignment === 'center' ? 'justifyCenter' :
      alignment === 'right' ? 'justifyRight' :
      'justifyFull';
    handleCommand(command);
  };

  // ---- Sütun (grid) ekleme ----
  const insertColumns = (ratios: number[]) => {
    // Boş, düzenlenebilir sütunlar — placeholder görünümü CSS ile sağlanır.
    // İçeride literal metin yok; içerik silinse bile sütun yapısı korunur.
    const cols = ratios
      .map(
        (r) =>
          `<div class="he-col" style="flex: ${r} 1 0px; min-width: 0;"><p><br></p></div>`
      )
      .join('');
    const rowHtml = `<div class="he-row" style="display:flex; flex-wrap:wrap; gap:16px; margin:16px 0;">${cols}</div><p><br></p>`;
    setShowColumnPicker(false);
    requestAnimationFrame(() => insertHtmlAtSelection(rowHtml));
  };

  // Seçili sütun kaplamasının konumunu hesapla.
  const updateColumnRect = (col: HTMLElement | null) => {
    const wrapper = wrapperRef.current;
    if (!col || !wrapper) {
      setColumnRect(null);
      return;
    }
    const colRect = col.getBoundingClientRect();
    const wrapRect = wrapper.getBoundingClientRect();
    setColumnRect({
      top: colRect.top - wrapRect.top + wrapper.scrollTop,
      left: colRect.left - wrapRect.left + wrapper.scrollLeft,
      width: colRect.width,
      height: colRect.height,
    });
  };

  const selectColumn = (col: HTMLElement) => {
    setSelectedColumn(col);
    updateColumnRect(col);
  };

  const clearColumnSelection = () => {
    setSelectedColumn(null);
    setColumnRect(null);
  };

  const deleteColumn = () => {
    if (!selectedColumn) return;
    const row = selectedColumn.closest('.he-row');
    selectedColumn.remove();
    // Sütun kalmadıysa satırı da kaldır.
    if (row && row.querySelectorAll('.he-col').length === 0) {
      row.remove();
    }
    emitChange();
    clearColumnSelection();
  };

  const deleteRow = () => {
    if (!selectedColumn) return;
    const row = selectedColumn.closest('.he-row');
    if (row) row.remove();
    emitChange();
    clearColumnSelection();
  };

  // ---- Resim seçimi, hizalama ve boyutlandırma ----

  // Seçili resmin çevresinde kaplamanın (tutamaç + hizalama araç çubuğu) konumunu hesapla.
  const updateOverlayRect = (img: HTMLImageElement | null) => {
    const wrapper = wrapperRef.current;
    if (!img || !wrapper) {
      setOverlayRect(null);
      return;
    }
    const imgRect = img.getBoundingClientRect();
    const wrapRect = wrapper.getBoundingClientRect();
    setOverlayRect({
      top: imgRect.top - wrapRect.top + wrapper.scrollTop,
      left: imgRect.left - wrapRect.left + wrapper.scrollLeft,
      width: imgRect.width,
      height: imgRect.height,
    });
  };

  const selectImage = (img: HTMLImageElement) => {
    setSelectedImage(img);
    updateOverlayRect(img);
  };

  const clearImageSelection = () => {
    setSelectedImage(null);
    setOverlayRect(null);
  };

  // Editör içine tıklanınca: resim/sütun seçimini yönet.
  const handleEditorClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      selectImage(target as HTMLImageElement);
      clearColumnSelection();
      return;
    }
    clearImageSelection();
    const col = (target.closest ? target.closest('.he-col') : null) as HTMLElement | null;
    if (col && editorRef.current && editorRef.current.contains(col)) {
      selectColumn(col);
    } else {
      clearColumnSelection();
    }
  };

  // Resme hizalama uygula — hem sınıf (ön yüz CSS'i için) hem inline stil (editör
  // önizlemesi ve yedek için) yazılır.
  const applyImageAlignment = (img: HTMLImageElement, align: ImageAlign) => {
    img.classList.remove('he-img-left', 'he-img-right', 'he-img-center');
    img.style.removeProperty('float');
    switch (align) {
      case 'left':
        img.classList.add('he-img-left');
        img.style.float = 'left';
        img.style.display = 'inline-block';
        img.style.margin = '8px 16px 8px 0';
        break;
      case 'right':
        img.classList.add('he-img-right');
        img.style.float = 'right';
        img.style.display = 'inline-block';
        img.style.margin = '8px 0 8px 16px';
        break;
      case 'center':
        img.classList.add('he-img-center');
        img.style.display = 'block';
        img.style.margin = '16px auto';
        break;
      case 'inline':
      default:
        img.style.display = 'inline-block';
        img.style.margin = '8px';
        break;
    }
    if (!img.style.maxWidth) img.style.maxWidth = '100%';
    emitChange();
    updateOverlayRect(img);
  };

  const handleQuickAlign = (align: ImageAlign) => {
    if (!selectedImage) return;
    applyImageAlignment(selectedImage, align);
  };

  // Köşe tutamacından sürükleyerek boyutlandırma
  const handleResizeStart = (event: React.PointerEvent) => {
    if (!selectedImage) return;
    event.preventDefault();
    event.stopPropagation();
    const naturalW = selectedImage.naturalWidth || selectedImage.width || 1;
    const naturalH = selectedImage.naturalHeight || selectedImage.height || 1;
    resizeStateRef.current = {
      startX: event.clientX,
      startWidth: selectedImage.getBoundingClientRect().width,
      aspect: naturalH > 0 ? naturalW / naturalH : 1,
    };
    window.addEventListener('pointermove', handleResizeMove);
    window.addEventListener('pointerup', handleResizeEnd);
  };

  const handleResizeMove = (event: PointerEvent) => {
    const state = resizeStateRef.current;
    const img = selectedImage;
    if (!state || !img) return;
    const delta = event.clientX - state.startX;
    const newWidth = Math.max(40, Math.round(state.startWidth + delta));
    img.style.width = `${newWidth}px`;
    img.style.height = 'auto';
    img.style.maxWidth = '100%';
    updateOverlayRect(img);
  };

  const handleResizeEnd = () => {
    resizeStateRef.current = null;
    window.removeEventListener('pointermove', handleResizeMove);
    window.removeEventListener('pointerup', handleResizeEnd);
    emitChange();
  };

  // Boyutlandırma diyaloğunu aç (seçili resim için hassas ayar)
  const openResizeDialog = () => {
    const img = selectedImage;
    if (!img) return;
    const currentWidth = img.style.width || `${img.width}px`;
    const currentHeight = img.style.height || `${img.height}px`;
    setImageWidth(currentWidth.replace('px', ''));
    setImageHeight(currentHeight.replace('px', ''));

    const naturalWidth = img.naturalWidth || img.width || 1;
    const naturalHeight = img.naturalHeight || img.height || 1;
    setOriginalAspectRatio(naturalHeight > 0 ? naturalWidth / naturalHeight : 1);

    if (img.classList.contains('he-img-left')) setImageAlign('left');
    else if (img.classList.contains('he-img-right')) setImageAlign('right');
    else if (img.classList.contains('he-img-center')) setImageAlign('center');
    else setImageAlign('inline');

    setImageResizeDialogOpen(true);
  };

  const applyImageResize = () => {
    if (!selectedImage) return;

    selectedImage.style.width = imageWidth ? `${imageWidth}px` : 'auto';
    selectedImage.style.height = imageHeight ? `${imageHeight}px` : 'auto';
    selectedImage.style.maxWidth = '100%';
    applyImageAlignment(selectedImage, imageAlign);

    emitChange();
    setImageResizeDialogOpen(false);
  };

  // Seçili resmi editörden tamamen kaldır.
  const removeSelectedImage = () => {
    if (!selectedImage) return;
    selectedImage.remove();
    emitChange();
    setImageResizeDialogOpen(false);
    clearImageSelection();
  };

  const safeAspect = (): number =>
    Number.isFinite(originalAspectRatio) && originalAspectRatio > 0 ? originalAspectRatio : 1;

  const handleWidthChange = (newWidth: string) => {
    setImageWidth(newWidth);
    if (maintainAspectRatio && newWidth) {
      const parsed = parseInt(newWidth);
      if (!Number.isNaN(parsed)) {
        setImageHeight(Math.round(parsed / safeAspect()).toString());
      }
    }
  };

  const handleHeightChange = (newHeight: string) => {
    setImageHeight(newHeight);
    if (maintainAspectRatio && newHeight) {
      const parsed = parseInt(newHeight);
      if (!Number.isNaN(parsed)) {
        setImageWidth(Math.round(parsed * safeAspect()).toString());
      }
    }
  };

  const resetImageSize = () => {
    if (!selectedImage) return;
    setImageWidth(selectedImage.naturalWidth.toString());
    setImageHeight(selectedImage.naturalHeight.toString());
  };

  const setImageSizePreset = (preset: 'small' | 'medium' | 'large' | 'full') => {
    if (!selectedImage) return;
    const naturalWidth = selectedImage.naturalWidth || selectedImage.width || 400;

    let newWidth: number;
    switch (preset) {
      case 'small':
        newWidth = Math.min(200, naturalWidth);
        break;
      case 'medium':
        newWidth = Math.min(400, naturalWidth);
        break;
      case 'large':
        newWidth = Math.min(600, naturalWidth);
        break;
      case 'full':
      default:
        newWidth = naturalWidth;
    }

    setImageWidth(newWidth.toString());
    setImageHeight(Math.round(newWidth / safeAspect()).toString());
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const insertLink = () => {
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      setLinkDialogOpen(false);
      setLinkUrl('');
      setLinkText('');
      // Diyalog kapanıp focus-trap serbest kaldıktan sonra ekle.
      requestAnimationFrame(() => insertHtmlAtSelection(linkHtml));
    }
  };

  const insertImage = async () => {
    try {
      setImageUploading(true);
      let imagesHtml = '';

      // URL modu
      if (imageUploadMode === 'url' && imageUrl) {
        imagesHtml = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto; margin: 8px; display: inline-block;" />`;
      }
      // Dosya yükleme modu - Image Service üzerinden (Firebase yerine güvenilir backend)
      else if (imageUploadMode === 'upload' && imageFiles.length > 0) {
        imageFiles.forEach((file, index) => {
          setUploadProgress(prev => ({ ...prev, [`${file.name}-${index}`]: 0 }));
        });

        const results = await ImageUploadService.uploadImages(imageFiles);

        imageFiles.forEach((file, index) => {
          setUploadProgress(prev => ({ ...prev, [`${file.name}-${index}`]: 100 }));
        });

        const htmls = results.map((res, idx) => {
          const url = res.originalUrl;
          const name = imageFiles[idx]?.name || 'image';
          const uploadedImage = { url, name, uploadDate: new Date().toISOString() };
          setUploadedImages(prev =>
            prev.some(img => img.url === url) ? prev : [...prev, uploadedImage]
          );
          return `<img src="${url}" alt="${imageAlt || name}" style="max-width: 100%; height: auto; margin: 8px; display: inline-block;" />`;
        });

        imagesHtml = htmls.join('\n');
      }

      if (imagesHtml) {
        // Önce formu/diyaloğu kapat, sonra imleç konumuna güvenle ekle.
        setImageUrl('');
        setImageAlt('');
        setImageFiles([]);
        setImageUploadMode('url');
        setUploadProgress({});
        setImageDialogOpen(false);

        const htmlToInsert = imagesHtml;
        requestAnimationFrame(() => insertHtmlAtSelection(htmlToInsert));
      }
    } catch (error) {
      console.error('Resimler yüklenirken hata:', error);
      alert('Resimler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: Dosya boyutu 5MB'dan küçük olmalıdır.`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Sadece resim dosyası kabul edilir.`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert('Bazı dosyalar yüklenemedi:\n\n' + errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
    }

    event.target.value = '';
  };

  const removeImageFile = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllImageFiles = () => {
    setImageFiles([]);
    setUploadProgress({});
  };

  const insertUploadedImage = (uploadedUrl: string, imageName: string) => {
    const imgHtml = `<img src="${uploadedUrl}" alt="${imageAlt || imageName}" style="max-width: 100%; height: auto; margin: 8px; display: inline-block;" />`;
    setImageUrl('');
    setImageAlt('');
    setImageFiles([]);
    setImageUploadMode('url');
    setUploadProgress({});
    setImageDialogOpen(false);
    requestAnimationFrame(() => insertHtmlAtSelection(imgHtml));
  };

  const deleteUploadedImage = (uploadedUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img.url !== uploadedUrl));
  };

  const insertTable = () => {
    let tableHtml = '<table border="1" style="border-collapse: collapse; width: 100%;">';
    for (let i = 0; i < tableRows; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        tableHtml += '<td style="padding: 8px; border: 1px solid #ddd;">Hücre</td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';
    setTableDialogOpen(false);
    requestAnimationFrame(() => insertHtmlAtSelection(tableHtml));
  };

  const handleColorSelect = (color: string, options?: { keepOpen?: boolean }) => {
    if (!editorRef.current) return;
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editorRef.current.style.color = color;
    } else {
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        document.execCommand('foreColor', false, color);
      } else {
        const span = document.createElement('span');
        span.style.color = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }

    emitChange();
    if (!options?.keepOpen) {
      setShowColorPicker(false);
    }
  };

  const handleBgColorSelect = (color: string, options?: { keepOpen?: boolean }) => {
    if (!editorRef.current) return;
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editorRef.current.style.backgroundColor = color;
    } else {
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        document.execCommand('backColor', false, color);
      } else {
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }

    emitChange();
    if (!options?.keepOpen) {
      setShowBgColorPicker(false);
    }
  };

  const handleFontSizeSelect = (size: number) => {
    if (!editorRef.current || !size) return;
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.getRangeAt(0).collapsed) {
      // Seçim yoksa: yeni yazılacak metin için imleç konumuna boş span ekle.
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.style.lineHeight = '1.2';
      span.appendChild(document.createTextNode('\u200B'));
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(span);
        const newRange = document.createRange();
        newRange.setStart(span.firstChild as Node, 1);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    } else {
      const range = selection.getRangeAt(0);
      // Seçili içeriği çıkar, İÇTEKİ tüm font-size/line-height stillerini temizle
      // (aksi halde iç içe span'lar yüzünden küçültme çalışmaz ve arka plan/satır
      // kutusu eski büyük boyutta kalır), sonra tek bir span ile yeniden sar.
      const fragment = range.extractContents();
      fragment.querySelectorAll('[style]').forEach((el) => {
        (el as HTMLElement).style.removeProperty('font-size');
        (el as HTMLElement).style.removeProperty('line-height');
      });
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.style.lineHeight = '1.2';
      span.appendChild(fragment);
      range.insertNode(span);

      // Seçimi yeni span üzerinde koru.
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.removeAllRanges();
      selection.addRange(newRange);
      savedRangeRef.current = newRange.cloneRange();
    }

    emitChange();
    setShowFontSizePicker(false);
    setCustomFontSize('');
  };

  // Click outside handlers
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
      if (bgColorPickerRef.current && !bgColorPickerRef.current.contains(event.target as Node)) {
        setShowBgColorPicker(false);
      }
      if (fontSizePickerRef.current && !fontSizePickerRef.current.contains(event.target as Node)) {
        setShowFontSizePicker(false);
      }
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target as Node)) {
        setShowColumnPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Seçili resim/sütunu takip et — içerik/pencere değişince kaplamayı yeniden konumlandır.
  React.useEffect(() => {
    if (!selectedImage && !selectedColumn) return;
    const reposition = () => {
      if (selectedImage) updateOverlayRect(selectedImage);
      if (selectedColumn) updateColumnRect(selectedColumn);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [selectedImage, selectedColumn]);

  return (
    <Box ref={wrapperRef} sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        p: 1,
        borderBottom: '1px solid #ddd',
        backgroundColor: '#f8f9fa',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Geri Al / İleri Al */}
        <Tooltip title="Geri Al">
          <IconButton size="small" onMouseDown={preventBlur} onClick={() => handleCommand('undo')} disabled={disabled || isCodeView}>
            <Undo />
          </IconButton>
        </Tooltip>
        <Tooltip title="İleri Al">
          <IconButton size="small" onMouseDown={preventBlur} onClick={() => handleCommand('redo')} disabled={disabled || isCodeView}>
            <Redo />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Font Boyutu — dropdown/select */}
        <Box sx={{ position: 'relative' }} ref={fontSizePickerRef}>
          <Tooltip title="Yazı Boyutu">
            <Button
              size="small"
              onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
              onClick={() => setShowFontSizePicker(!showFontSizePicker)}
              disabled={disabled || isCodeView}
              startIcon={<FormatSize fontSize="small" />}
              endIcon={<ArrowDropDown fontSize="small" />}
              sx={{ minWidth: 'auto', px: 1, textTransform: 'none', color: 'text.primary' }}
            >
              Boyut
            </Button>
          </Tooltip>
          {showFontSizePicker && (
            <Paper sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              p: 1,
              minWidth: 140
            }}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="px"
                  value={customFontSize}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setCustomFontSize(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = parseInt(customFontSize);
                      if (!Number.isNaN(v) && v > 0) handleFontSizeSelect(v);
                    }
                  }}
                  sx={{ width: 80 }}
                  InputProps={{ inputProps: { min: 1, max: 200 } }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onMouseDown={preventBlur}
                  onClick={() => {
                    const v = parseInt(customFontSize);
                    if (!Number.isNaN(v) && v > 0) handleFontSizeSelect(v);
                  }}
                >
                  Uygula
                </Button>
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {fontSizes.map(size => (
                  <Button
                    key={size}
                    fullWidth
                    size="small"
                    onMouseDown={preventBlur}
                    onClick={() => handleFontSizeSelect(size)}
                    sx={{ justifyContent: 'flex-start', fontSize: `${Math.min(size, 18)}px`, color: 'text.primary' }}
                  >
                    {size}px
                  </Button>
                ))}
              </Box>
            </Paper>
          )}
        </Box>

        {/* Metin Formatı */}
        <Tooltip title="Kalın">
          <IconButton size="small" color={activeFormats.bold ? 'primary' : 'default'} onMouseDown={preventBlur} onClick={() => handleCommand('bold')} disabled={disabled || isCodeView} sx={activeFormats.bold ? { backgroundColor: '#e3f2fd' } : undefined}>
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="İtalik">
          <IconButton size="small" color={activeFormats.italic ? 'primary' : 'default'} onMouseDown={preventBlur} onClick={() => handleCommand('italic')} disabled={disabled || isCodeView} sx={activeFormats.italic ? { backgroundColor: '#e3f2fd' } : undefined}>
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Altı Çizili">
          <IconButton size="small" color={activeFormats.underline ? 'primary' : 'default'} onMouseDown={preventBlur} onClick={() => handleCommand('underline')} disabled={disabled || isCodeView} sx={activeFormats.underline ? { backgroundColor: '#e3f2fd' } : undefined}>
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        {/* Renk Seçiciler */}
        <Box sx={{ position: 'relative' }} ref={colorPickerRef}>
          <Tooltip title="Metin Rengi">
            <IconButton
              size="small"
              onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
              onClick={() => {
                setShowBgColorPicker(false);
                setShowColorPicker(!showColorPicker);
              }}
              disabled={disabled || isCodeView}
            >
              <FormatColorText />
            </IconButton>
          </Tooltip>
          {showColorPicker && (
            <Paper sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              p: 1.25,
              width: 220
            }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                Hazır renkler
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, mb: 1 }}>
                {colors.map(color => (
                  <Box
                    key={color}
                    onMouseDown={preventBlur}
                    onClick={() => {
                      setCustomTextColor(color);
                      handleColorSelect(color);
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: '1px solid #ccc',
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  />
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                Özel renk paleti
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input"
                  ref={textColorInputRef}
                  type="color"
                  value={customTextColor}
                  onMouseDown={(e: React.MouseEvent) => {
                    preventBlur(e);
                    saveSelection();
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomTextColor(e.target.value);
                  }}
                  sx={{
                    width: 36,
                    height: 28,
                    p: 0,
                    border: '1px solid #ccc',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    background: 'none',
                  }}
                />
                <TextField
                  size="small"
                  value={customTextColor}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setCustomTextColor(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && /^#[0-9A-Fa-f]{6}$/.test(customTextColor)) {
                      handleColorSelect(customTextColor);
                    }
                  }}
                  placeholder="#000000"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 7, style: { fontSize: 12, fontFamily: 'monospace' } }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
                  onClick={() => {
                    if (/^#[0-9A-Fa-f]{6}$/.test(customTextColor)) {
                      handleColorSelect(customTextColor);
                    }
                  }}
                  sx={{ minWidth: 'auto', px: 1, textTransform: 'none' }}
                >
                  Uygula
                </Button>
              </Box>
            </Paper>
          )}
        </Box>

        <Box sx={{ position: 'relative' }} ref={bgColorPickerRef}>
          <Tooltip title="Arka Plan Rengi">
            <IconButton
              size="small"
              onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
              onClick={() => {
                setShowColorPicker(false);
                setShowBgColorPicker(!showBgColorPicker);
              }}
              disabled={disabled || isCodeView}
            >
              <FormatColorFill />
            </IconButton>
          </Tooltip>
          {showBgColorPicker && (
            <Paper sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              p: 1.25,
              width: 220
            }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                Hazır renkler
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, mb: 1 }}>
                {colors.map(color => (
                  <Box
                    key={`bg-${color}`}
                    onMouseDown={preventBlur}
                    onClick={() => {
                      setCustomBgColor(color);
                      handleBgColorSelect(color);
                    }}
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: '1px solid #ccc',
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                  />
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                Özel renk paleti
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input"
                  ref={bgColorInputRef}
                  type="color"
                  value={customBgColor}
                  onMouseDown={(e: React.MouseEvent) => {
                    preventBlur(e);
                    saveSelection();
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomBgColor(e.target.value);
                  }}
                  sx={{
                    width: 36,
                    height: 28,
                    p: 0,
                    border: '1px solid #ccc',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    background: 'none',
                  }}
                />
                <TextField
                  size="small"
                  value={customBgColor}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setCustomBgColor(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && /^#[0-9A-Fa-f]{6}$/.test(customBgColor)) {
                      handleBgColorSelect(customBgColor);
                    }
                  }}
                  placeholder="#FFFF00"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 7, style: { fontSize: 12, fontFamily: 'monospace' } }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
                  onClick={() => {
                    if (/^#[0-9A-Fa-f]{6}$/.test(customBgColor)) {
                      handleBgColorSelect(customBgColor);
                    }
                  }}
                  sx={{ minWidth: 'auto', px: 1, textTransform: 'none' }}
                >
                  Uygula
                </Button>
              </Box>
            </Paper>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Hizalama */}
        <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Tooltip title="Sola Hizala">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              onClick={() => handleContentAlignment('left')}
              disabled={disabled || isCodeView}
              sx={{ borderRadius: 0, borderRight: '1px solid #ddd', '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              <FormatAlignLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ortala">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              onClick={() => handleContentAlignment('center')}
              disabled={disabled || isCodeView}
              sx={{ borderRadius: 0, borderRight: '1px solid #ddd', '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              <FormatAlignCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sağa Hizala">
            <IconButton
              size="small"
              onMouseDown={preventBlur}
              onClick={() => handleContentAlignment('right')}
              disabled={disabled || isCodeView}
              sx={{ borderRadius: 0, '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              <FormatAlignRight />
            </IconButton>
          </Tooltip>
        </Box>

        {/* İki Yana Yasla */}
        <Tooltip title="İki Yana Yasla">
          <IconButton size="small" onMouseDown={preventBlur} onClick={() => handleContentAlignment('justify')} disabled={disabled || isCodeView}>
            <FormatAlignJustify />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Sütun / Grid düzeni */}
        <Box sx={{ position: 'relative' }} ref={columnPickerRef}>
          <Tooltip title="Sütun Ekle">
            <Button
              size="small"
              onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              disabled={disabled || isCodeView}
              startIcon={<ViewColumn fontSize="small" />}
              endIcon={<ArrowDropDown fontSize="small" />}
              sx={{ minWidth: 'auto', px: 1, textTransform: 'none', color: 'text.primary' }}
            >
              Sütun
            </Button>
          </Tooltip>
          {showColumnPicker && (
            <Paper sx={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, p: 0.5, minWidth: 240 }}>
              {COLUMN_LAYOUTS.map(layout => (
                <Button
                  key={layout.id}
                  fullWidth
                  size="small"
                  onMouseDown={preventBlur}
                  onClick={() => insertColumns(layout.ratios)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary', py: 0.75 }}
                >
                  <Box sx={{ display: 'flex', gap: '3px', width: 46, mr: 1.5 }}>
                    {layout.ratios.map((r, i) => (
                      <Box key={i} sx={{ flex: `${r} 1 0`, height: 18, backgroundColor: '#bbdefb', borderRadius: '2px' }} />
                    ))}
                  </Box>
                  {layout.label}
                </Button>
              ))}
            </Paper>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Listeler */}
        <Tooltip title="Madde İşaretli Liste">
          <IconButton size="small" color={activeFormats.insertUnorderedList ? 'primary' : 'default'} onMouseDown={preventBlur} onClick={() => handleCommand('insertUnorderedList')} disabled={disabled || isCodeView} sx={activeFormats.insertUnorderedList ? { backgroundColor: '#e3f2fd' } : undefined}>
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numaralı Liste">
          <IconButton size="small" color={activeFormats.insertOrderedList ? 'primary' : 'default'} onMouseDown={preventBlur} onClick={() => handleCommand('insertOrderedList')} disabled={disabled || isCodeView} sx={activeFormats.insertOrderedList ? { backgroundColor: '#e3f2fd' } : undefined}>
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        {/* Alıntı */}
        <Tooltip title="Alıntı">
          <IconButton size="small" onMouseDown={preventBlur} onClick={() => handleCommand('formatBlock', '<blockquote>')} disabled={disabled || isCodeView}>
            <FormatQuote />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Başlıklar */}
        <Tooltip title="Başlık 1">
          <Button size="small" onMouseDown={preventBlur} onClick={() => handleCommand('formatBlock', '<h1>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H1
          </Button>
        </Tooltip>
        <Tooltip title="Başlık 2">
          <Button size="small" onMouseDown={preventBlur} onClick={() => handleCommand('formatBlock', '<h2>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H2
          </Button>
        </Tooltip>
        <Tooltip title="Başlık 3">
          <Button size="small" onMouseDown={preventBlur} onClick={() => handleCommand('formatBlock', '<h3>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H3
          </Button>
        </Tooltip>
        <Tooltip title="Paragraf">
          <Button size="small" onMouseDown={preventBlur} onClick={() => handleCommand('formatBlock', '<p>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            P
          </Button>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link, Resim, Tablo */}
        <Tooltip title="Link Ekle">
          <IconButton
            size="small"
            onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
            onClick={() => setLinkDialogOpen(true)}
            disabled={disabled || isCodeView}
          >
            <LinkIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Resim Ekle">
          <IconButton
            size="small"
            onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
            onClick={() => setImageDialogOpen(true)}
            disabled={disabled || isCodeView}
          >
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Tablo Ekle">
          <IconButton
            size="small"
            onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
            onClick={() => setTableDialogOpen(true)}
            disabled={disabled || isCodeView}
          >
            <TableChart />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* HTML Kod Görünümü */}
        <Tooltip title={isCodeView ? "Görsel Editör" : "HTML Kodu"}>
          <IconButton
            size="small"
            onClick={() => setIsCodeView(!isCodeView)}
            disabled={disabled}
            color={isCodeView ? "primary" : "default"}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content */}
      {isCodeView ? (
        <TextField
          multiline
          rows={12}
          value={value}
          onChange={handleCodeChange}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-root': {
              p: 2,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '13px',
              lineHeight: 1.4
            }
          }}
          fullWidth
        />
      ) : (
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleContentChange}
          onClick={handleEditorClick}
          onFocus={() => { isFocusedRef.current = true; }}
          onBlur={() => { isFocusedRef.current = false; emitChange(); }}
          onKeyUp={handleSelectionActivity}
          onMouseUp={handleSelectionActivity}
          suppressContentEditableWarning
          sx={{
            minHeight: '300px',
            p: 2,
            outline: 'none',
            lineHeight: 1.6,
            '&:focus': {
              backgroundColor: '#fafafa'
            },
            '&[contenteditable="false"]': {
              backgroundColor: '#f5f5f5',
              color: '#666'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '16px 0 8px 0',
              fontWeight: 'bold'
            },
            '& p': {
              margin: '8px 0'
            },
            '& ul, & ol': {
              margin: '8px 0',
              paddingLeft: '24px'
            },
            '& blockquote': {
              margin: '16px 0',
              padding: '12px 16px',
              borderLeft: '4px solid #ddd',
              backgroundColor: '#f9f9f9',
              fontStyle: 'italic'
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: '16px 0'
            },
            '& td, & th': {
              padding: '8px',
              border: '1px solid #ddd'
            },
            // Sütun (grid) düzeni — editör önizlemesi
            '& .he-row': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              margin: '16px 0',
              alignItems: 'flex-start'
            },
            '& .he-col': {
              flex: '1 1 0',
              minWidth: 0,
              minHeight: '56px',
              border: '1px dashed #cbd5e1',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: '#fbfcfe',
              transition: 'border-color 0.15s ease'
            },
            '& .he-col img': {
              display: 'block',
              maxWidth: 'min(240px, 100%)',
              width: 'auto',
              height: 'auto',
              borderRadius: '0.5rem'
            },
            // Boş sütun için placeholder görünümü (yalnızca içi boşken).
            '& .he-col:empty::before, & .he-col > p:only-child:empty::before': {
              content: '"İçerik ekleyin..."',
              color: '#9ca3af',
              fontStyle: 'italic',
              pointerEvents: 'none'
            },
            '@media (max-width: 768px)': {
              '& .he-row': { flexDirection: 'column' }
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: 'box-shadow 0.2s ease',
              '&:hover': {
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
              }
            },
            '& img.he-img-left': { float: 'left', margin: '8px 16px 8px 0' },
            '& img.he-img-right': { float: 'right', margin: '8px 0 8px 16px' },
            '& img.he-img-center': { display: 'block', margin: '16px auto', float: 'none' }
          }}
        />
      )}

      {/* Seçili resim kaplaması — köşe tutamacı + hızlı hizalama araç çubuğu */}
      {overlayRect && selectedImage && !isCodeView && (
        <Box
          sx={{
            position: 'absolute',
            top: overlayRect.top,
            left: overlayRect.left,
            width: overlayRect.width,
            height: overlayRect.height,
            border: '2px solid #1976d2',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 20
          }}
        >
          {/* Hızlı hizalama araç çubuğu */}
          <Box
            onMouseDown={preventBlur}
            sx={{
              position: 'absolute',
              top: -40,
              left: 0,
              display: 'flex',
              gap: 0.25,
              backgroundColor: '#1976d2',
              borderRadius: '4px',
              p: 0.25,
              pointerEvents: 'auto',
              boxShadow: 2
            }}
          >
            <Tooltip title="Sola Yasla (metin sağdan aksın)">
              <IconButton size="small" onClick={() => handleQuickAlign('left')} sx={{ color: 'white', p: 0.5 }}>
                <FormatAlignLeft fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ortala">
              <IconButton size="small" onClick={() => handleQuickAlign('center')} sx={{ color: 'white', p: 0.5 }}>
                <FormatAlignCenter fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sağa Yasla (metin soldan aksın)">
              <IconButton size="small" onClick={() => handleQuickAlign('right')} sx={{ color: 'white', p: 0.5 }}>
                <FormatAlignRight fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Boyut / Detay">
              <IconButton size="small" onClick={openResizeDialog} sx={{ color: 'white', p: 0.5 }}>
                <PhotoSizeSelectActual fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sil">
              <IconButton size="small" onClick={removeSelectedImage} sx={{ color: 'white', p: 0.5 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Köşe boyutlandırma tutamacı */}
          <Box
            onPointerDown={handleResizeStart}
            sx={{
              position: 'absolute',
              right: -7,
              bottom: -7,
              width: 14,
              height: 14,
              backgroundColor: '#1976d2',
              border: '2px solid #fff',
              borderRadius: '50%',
              cursor: 'nwse-resize',
              pointerEvents: 'auto'
            }}
          />
        </Box>
      )}

      {/* Seçili sütun kaplaması — sütun / satır silme */}
      {columnRect && selectedColumn && !isCodeView && (
        <Box
          sx={{
            position: 'absolute',
            top: columnRect.top,
            left: columnRect.left,
            width: columnRect.width,
            height: columnRect.height,
            border: '2px solid #7c3aed',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 19
          }}
        >
          <Box
            onMouseDown={preventBlur}
            sx={{
              position: 'absolute',
              top: -32,
              right: 0,
              display: 'flex',
              gap: 0.25,
              backgroundColor: '#7c3aed',
              borderRadius: '4px',
              p: 0.25,
              pointerEvents: 'auto',
              boxShadow: 2
            }}
          >
            <Tooltip title="Sütunu Sil">
              <IconButton size="small" onClick={deleteColumn} sx={{ color: 'white', p: 0.5 }}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Tüm Satırı (Sütun Grubunu) Sil">
              <IconButton size="small" onClick={deleteRow} sx={{ color: 'white', p: 0.5 }}>
                <DeleteSweep fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {!value && !isCodeView && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            position: 'absolute',
            top: '80px',
            left: '16px',
            pointerEvents: 'none',
            opacity: 0.6
          }}
        >
          {placeholder}
        </Typography>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Ekle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Link Metni"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>İptal</Button>
          <Button onClick={insertLink} variant="contained" disabled={!linkUrl || !linkText}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Resim Ekle
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mod Seçimi */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resim Ekleme Yöntemi
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant={imageUploadMode === 'url' ? 'contained' : 'outlined'}
                startIcon={<InsertLink />}
                onClick={() => {
                  setImageUploadMode('url');
                  setShowUploadedImages(false);
                }}
                size="small"
              >
                URL ile Ekle
              </Button>
              <Button
                variant={imageUploadMode === 'upload' ? 'contained' : 'outlined'}
                startIcon={<CloudUpload />}
                onClick={() => {
                  setImageUploadMode('upload');
                  setShowUploadedImages(false);
                }}
                size="small"
              >
                Dosya Yükle
              </Button>
              {uploadedImages.length > 0 && (
                <Button
                  variant={showUploadedImages ? 'contained' : 'outlined'}
                  startIcon={<History />}
                  onClick={() => {
                    setShowUploadedImages(!showUploadedImages);
                    if (!showUploadedImages) {
                      setImageUploadMode('url');
                    }
                  }}
                  size="small"
                  color="secondary"
                >
                  Yüklenen Resimler ({uploadedImages.length})
                </Button>
              )}
            </Box>
          </Box>

          {/* URL Modu */}
          {imageUploadMode === 'url' && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Resim URL"
                fullWidth
                variant="outlined"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                sx={{ mb: 2 }}
                helperText="Resmin internet adresini girin"
              />
            </Box>
          )}

          {/* Dosya Yükleme Modu */}
          {imageUploadMode === 'upload' && (
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload-input"
                type="file"
                multiple
                onChange={handleImageFilesChange}
              />
              <label htmlFor="image-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{
                    mb: 2,
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  {imageFiles.length > 0
                    ? `${imageFiles.length} Resim Seçildi - Daha Fazla Ekle`
                    : 'Resim Dosyalarını Seç (Çoklu Seçim)'}
                </Button>
              </label>

              {imageFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Seçilen Resimler ({imageFiles.length})
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={clearAllImageFiles}
                      startIcon={<DeleteIcon />}
                    >
                      Tümünü Temizle
                    </Button>
                  </Box>

                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    {imageFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          mb: 1,
                          backgroundColor: '#f9f9f9',
                          borderRadius: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            📷 {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          {uploadProgress[`${file.name}-${index}`] !== undefined && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="primary.main">
                                Yükleniyor: %{uploadProgress[`${file.name}-${index}`]}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeImageFile(index)}
                          disabled={imageUploading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                • <strong>Çoklu seçim:</strong> Ctrl/Cmd tuşu ile birden fazla resim seçebilirsiniz<br/>
                • <strong>Maksimum dosya boyutu:</strong> Her dosya için 5MB<br/>
                • <strong>Desteklenen formatlar:</strong> JPG, PNG, GIF, WebP
              </Typography>
            </Box>
          )}

          {/* Yüklenen Resimler Bölümü */}
          {showUploadedImages && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Daha Önce Yüklenen Resimler:
              </Typography>

              {uploadedImages.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  border: '2px dashed #ddd',
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9'
                }}>
                  <AddPhotoAlternate sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Henüz resim yüklenmemiş
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dosya yükleyerek resimleriniz burada görünecek
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fafafa'
                }}>
                  <Grid container spacing={2}>
                    {uploadedImages.map((image, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`${image.url}-${index}`}>
                        <Card sx={{ position: 'relative', '&:hover': { boxShadow: 3 } }}>
                          <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                            <img
                              src={image.url}
                              alt={image.name}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px 4px 0 0'
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '11px'
                              }}
                              title={image.name}
                            >
                              {image.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                              {new Date(image.uploadDate).toLocaleDateString('tr-TR')}
                            </Typography>
                          </CardContent>

                          {/* Hover Butonları */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            borderRadius: 1,
                            '&:hover': { opacity: 1 }
                          }}>
                            <Tooltip title="Resmi Ekle">
                              <IconButton
                                size="small"
                                onClick={() => insertUploadedImage(image.url, image.name)}
                                sx={{
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <AddPhotoAlternate fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Listeden Sil">
                              <IconButton
                                size="small"
                                onClick={() => deleteUploadedImage(image.url)}
                                sx={{
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 <strong>İpucu:</strong> Editörden sildiğiniz resimleri buradan tekrar ekleyebilirsiniz.
                Resmin üzerine gelerek "Resmi Ekle" butonuna tıklayın.
              </Typography>
            </Box>
          )}

          {/* Alt Metin */}
          <TextField
            margin="dense"
            label="Alt Metin (Opsiyonel)"
            fullWidth
            variant="outlined"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            placeholder="Resim açıklaması"
            helperText="SEO ve erişilebilirlik için önerilir"
          />

          {/* Önizleme */}
          {((imageUploadMode === 'url' && imageUrl) || (imageUploadMode === 'upload' && imageFiles.length > 0)) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Önizleme:
              </Typography>
              <Box sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                backgroundColor: '#fafafa',
                maxHeight: 300,
                overflow: 'auto'
              }}>
                {imageUploadMode === 'url' && imageUrl ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img
                      src={imageUrl}
                      alt={imageAlt || 'Önizleme'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                ) : imageFiles.length > 0 ? (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 2
                  }}>
                    {imageFiles.map((file, index) => (
                      <Box
                        key={`preview-${file.name}-${index}`}
                        sx={{
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: '100%',
                            height: '80px',
                            objectFit: 'cover'
                          }}
                        />
                        <Box sx={{ p: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          onClick={() => removeImageFile(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImageDialogOpen(false);
              setImageUrl('');
              setImageAlt('');
              setImageFiles([]);
              setImageUploadMode('url');
              setUploadProgress({});
              setShowUploadedImages(false);
            }}
            disabled={imageUploading}
          >
            İptal
          </Button>
          <Button
            onClick={insertImage}
            variant="contained"
            disabled={
              imageUploading ||
              (imageUploadMode === 'url' && !imageUrl) ||
              (imageUploadMode === 'upload' && imageFiles.length === 0)
            }
            startIcon={imageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
          >
            {imageUploading
              ? `Yükleniyor... (${imageFiles.length} resim)`
              : imageUploadMode === 'upload' && imageFiles.length > 1
                ? `${imageFiles.length} Resmi Ekle`
                : 'Resmi Ekle'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tablo Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Satır Sayısı"
                type="number"
                fullWidth
                variant="outlined"
                value={tableRows}
                onChange={(e) => setTableRows(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 20 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sütun Sayısı"
                type="number"
                fullWidth
                variant="outlined"
                value={tableCols}
                onChange={(e) => setTableCols(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>İptal</Button>
          <Button onClick={insertTable} variant="contained">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Boyutlandırma / Hizalama Dialog */}
      <Dialog
        open={imageResizeDialogOpen}
        onClose={() => setImageResizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoSizeSelectActual />
            Resim Boyutu ve Hizalama
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box>
              {/* Mevcut Resim Önizleme */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Mevcut Resim:
                </Typography>
                <Box sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fafafa',
                  display: 'inline-block'
                }}>
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt || 'Seçilen resim'}
                    style={{
                      maxWidth: '200px',
                      maxHeight: '150px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Orijinal Boyut: {selectedImage.naturalWidth} × {selectedImage.naturalHeight} px
                </Typography>
              </Box>

              {/* Hizalama Seçenekleri */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Hizalama:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={imageAlign === 'left' ? 'contained' : 'outlined'}
                    startIcon={<FormatAlignLeft />}
                    onClick={() => setImageAlign('left')}
                  >
                    Sola Yasla
                  </Button>
                  <Button
                    size="small"
                    variant={imageAlign === 'center' ? 'contained' : 'outlined'}
                    startIcon={<FormatAlignCenter />}
                    onClick={() => setImageAlign('center')}
                  >
                    Ortala
                  </Button>
                  <Button
                    size="small"
                    variant={imageAlign === 'right' ? 'contained' : 'outlined'}
                    startIcon={<FormatAlignRight />}
                    onClick={() => setImageAlign('right')}
                  >
                    Sağa Yasla
                  </Button>
                  <Button
                    size="small"
                    variant={imageAlign === 'inline' ? 'contained' : 'outlined'}
                    onClick={() => setImageAlign('inline')}
                  >
                    Satır İçi
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Sola/Sağa yasla seçeneğinde metin resmin yanından akar (float). Ortala seçeneğinde resim ortalanır.
                </Typography>
              </Box>

              {/* Hızlı Boyut Seçenekleri */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Hızlı Boyut Seçenekleri:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => setImageSizePreset('small')}>
                    Küçük (200px)
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setImageSizePreset('medium')}>
                    Orta (400px)
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setImageSizePreset('large')}>
                    Büyük (600px)
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setImageSizePreset('full')}>
                    Orijinal
                  </Button>
                  <Button size="small" variant="outlined" color="secondary" onClick={resetImageSize}>
                    Sıfırla
                  </Button>
                </Box>
              </Box>

              {/* Manuel Boyut Ayarları */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Manuel Boyut Ayarları:
                </Typography>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Genişlik (px)"
                      type="number"
                      value={imageWidth}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{ inputProps: { min: 1, max: 2000 } }}
                    />
                  </Grid>

                  <Grid item xs={2} sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                      color={maintainAspectRatio ? 'primary' : 'default'}
                      title={maintainAspectRatio ? 'Oranı koru (Açık)' : 'Oranı koru (Kapalı)'}
                    >
                      <AspectRatio />
                    </IconButton>
                  </Grid>

                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Yükseklik (px)"
                      type="number"
                      value={imageHeight}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{ inputProps: { min: 1, max: 2000 } }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AspectRatio color={maintainAspectRatio ? 'primary' : 'disabled'} />
                  <Typography variant="body2" color={maintainAspectRatio ? 'primary' : 'text.secondary'}>
                    {maintainAspectRatio ? 'En-boy oranı korunuyor' : 'En-boy oranı serbest'}
                  </Typography>
                </Box>
              </Box>

              {/* Uyarılar */}
              {imageWidth && parseInt(imageWidth) > selectedImage.naturalWidth && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ Resmi orijinal boyutundan büyük yapıyorsunuz. Bu kalite kaybına neden olabilir.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={removeSelectedImage} color="error" startIcon={<DeleteIcon />}>
            Resmi Kaldır
          </Button>
          <Box>
            <Button onClick={() => setImageResizeDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={applyImageResize}
              variant="contained"
              startIcon={<PhotoSizeSelectActual />}
              sx={{ ml: 1 }}
            >
              Uygula
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HTMLEditor;
